import React from 'react';
import { NavMenuPrimitives } from '@abyss/web/ui/NavMenu';
import { MenuModel } from '@src/api/web-services/layout/MenuService/MenuTypes';

interface DynamicMenuItemProps {
  item: MenuModel;
}

interface DynamicMenuListProps {
  menuItems: MenuModel[];
  loading: boolean;
  error: string | null;
}

// Shared styles for menu links
const menuLinkStyles = {
  color: '$link.color.text.primary',
  fontWeight: '$bold',
  fontSize: '$xs',
  textDecoration: 'none',
  '&:hover': {
    color: '$link.color.text.primary',
  },
} as const;

// Helper function to get description for menu items
const getMenuItemDescription = (title: string): string => {
  const descriptions: Record<string, string> = {
    Category:
      'Organize reports by topic or function to simplify browsing and discovery.',
    Role: 'Define access levels based on user permissions and responsibilities.',
  };
  return descriptions[title] || `Access ${title.toLowerCase()} functionality.`;
};

const DynamicMenuItem: React.FC<DynamicMenuItemProps> = ({ item }) => {
  // If item has children, render as dropdown
  if (item.hasChildren && item.children && item.children.length > 0) {
    return (
      <NavMenuPrimitives.Item>
        <NavMenuPrimitives.Trigger
          css={{
            ...menuLinkStyles,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            '&[data-state="open"]': {
              color: '$link.color.text.primary',
            },
          }}
        >
          {item.title}
        </NavMenuPrimitives.Trigger>
        <NavMenuPrimitives.Content>
          <NavMenuPrimitives.Columns>
            <NavMenuPrimitives.Column title="">
              {item.children
                .filter((child) => child.isActive)
                .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
                .map((child) => (
                  <NavMenuPrimitives.MenuItem
                    key={child.id}
                    title={child.title}
                    href={child.url || '#'}
                    description={getMenuItemDescription(child.title)}
                  />
                ))}
            </NavMenuPrimitives.Column>
          </NavMenuPrimitives.Columns>
        </NavMenuPrimitives.Content>
      </NavMenuPrimitives.Item>
    );
  }

  // Regular menu item
  return (
    <NavMenuPrimitives.Link href={item.url || '#'} css={menuLinkStyles}>
      {item.title}
    </NavMenuPrimitives.Link>
  );
};

export const DynamicMenuList: React.FC<DynamicMenuListProps> = ({
  menuItems,
  loading,
  error,
}) => {
  if (loading) {
    return (
      <NavMenuPrimitives.Link css={menuLinkStyles}>
        Loading...
      </NavMenuPrimitives.Link>
    );
  }

  if (error) {
    // Fallback to static menu items if API fails
    return (
      <>
        <NavMenuPrimitives.Link href="/inventory" css={menuLinkStyles}>
          Reports
        </NavMenuPrimitives.Link>
        <NavMenuPrimitives.Link href="/history" css={menuLinkStyles}>
          History
        </NavMenuPrimitives.Link>
      </>
    );
  }

  // Filter top-level menu items (no parentId)
  const topLevelItems = menuItems.filter((item) => !item.parentId);

  return (
    <>
      {topLevelItems.map((item) => (
        <DynamicMenuItem key={item.id} item={item} />
      ))}
    </>
  );
};
